import { computed, ref, type ComputedRef } from 'vue';
import type { RouteLocationRaw } from 'vue-router';
import { useUserConfig } from '@/store/user-config';
import { useAgentChat } from '@/store/agent-chat';
import { adminGroupOf, canSeeAdmin, useVisibleAdminTabs } from '@lib/admin-nav';
import { useMChartsLabel } from '@lib/mchart-label';
import { chatPanel } from '@lib/chat-panel';
import { focusProductSearch } from '@lib/product-search';
import { shortcutHelp } from '@lib/shortcut-help';
import { isMac } from '@lib/shortcuts';
import { useOtherPlmBrands, switchBrand, switchBrandInNewTab } from '@lib/brand-switch';
import { entitySearchKinds } from '@lib/palette-entity-search';
import { blobUrl } from '@lib/api';
import type { EntitySearchType } from '@/ApiClient';

import { componentsIconScale, navIcons } from '@lib/nav-icons';
import chatIcon from '@icons/filters-icons/comment-outlined.svg';
import searchIcon from '@icons/other/ico-search.svg';
import questionMarkIcon from '@icons/other/question-mark-circle.svg';

/**
 * Which list the palette is showing. The root menu can hand off to a sub-menu, which
 * replaces the entry list (and, for an entity search, the source of those entries)
 * without the palette ever closing.
 */
export type PaletteView = { id: 'root' } | { id: 'brands' } | { id: 'search'; entity: EntitySearchType };

const isOpen = ref(false);
const view = ref<PaletteView>({ id: 'root' });
// Whether the root menu is behind the current view. A sub-menu opened straight from a
// shortcut has nothing behind it, so Escape closes rather than dropping the user into a
// menu they never asked for.
const cameFromRoot = ref(false);

export const commandPalette = {
  isOpen,
  view,
  cameFromRoot,
  open: () => {
    view.value = { id: 'root' };
    cameFromRoot.value = false;
    isOpen.value = true;
  },
  /** Open straight into a sub-menu, the way the `s …` shortcuts do. */
  openAt: (next: PaletteView) => {
    view.value = next;
    cameFromRoot.value = false;
    isOpen.value = true;
  },
  close: () => {
    isOpen.value = false;
  },
  toggle: () => {
    if (isOpen.value) isOpen.value = false;
    else commandPalette.open();
  },
  enter: (next: PaletteView) => {
    cameFromRoot.value = view.value.id === 'root';
    view.value = next;
  },
  /** One level up. The sub-menus are one deep, so "up" is always the root. */
  back: () => {
    cameFromRoot.value = false;
    view.value = { id: 'root' };
  },
};

export type PaletteGroup = 'Navigate' | 'Search' | 'Admin' | 'Actions' | 'Brands' | 'Results';

interface PaletteEntryBase {
  id: string;
  label: string;
  group: PaletteGroup;
  /** Second line under the label — a product number, a supplier name. */
  detail?: string;
  icon?: string;
  /** Artwork drawn inside its own padding, scaled up so it fills the row's icon box like the rest. */
  iconScale?: number;
  /** Thumbnail URL drawn in place of `icon`; `icon` is the fallback if it fails to load. */
  image?: string;
  /** Display combo for the entry's own shortcut, e.g. 'g p' or '/'. */
  shortcut?: string;
  /** Extra filter text beyond the label — synonyms, the admin group name. */
  keywords?: string;
  /**
   * What Ctrl/Cmd+Enter does for an entry that acts rather than navigates. A navigating entry
   * gets new-tab handling from its own route; this is how the rest opt in.
   */
  runInNewTab?: () => void;
}

/**
 * An entry either navigates, opens a sub-menu, or runs an action. Navigating entries expose
 * the destination rather than pushing it themselves, so the palette can render them as real
 * links — copyable, and openable in a new tab or window.
 */
export type PaletteEntry = PaletteEntryBase &
  (
    | { to: () => RouteLocationRaw; enter?: never; run?: never }
    | { to?: never; enter: PaletteView; run?: never }
    | { to?: never; enter?: never; run: () => void }
  );

/** Same logo the navbar's brand menu draws; rows fall back to their icon when it 404s. */
function brandLogoUrl(brandId: number) {
  return `${blobUrl}/public/brand-logos/${brandId}.png`;
}

/**
 * The brands the user can switch to, as palette entries. Used both as the 'brands'
 * sub-menu and — when there is exactly one other brand — inlined into the root menu,
 * where a sub-menu holding a single row would be pure ceremony.
 */
export function useBrandEntries(): ComputedRef<PaletteEntry[]> {
  const otherBrands = useOtherPlmBrands();

  return computed(() =>
    otherBrands.value.map((brand) => ({
      id: `brand-${brand.brandID}`,
      label: brand.name,
      group: 'Brands' as const,
      icon: navIcons.brand,
      image: brandLogoUrl(brand.brandID),
      run: () => switchBrand(brand.brandID),
      runInNewTab: () => switchBrandInNewTab(brand.brandID),
    }))
  );
}

export function usePaletteEntries(): ComputedRef<PaletteEntry[]> {
  const adminTabs = useVisibleAdminTabs();
  const mChartsLabel = useMChartsLabel();
  const otherBrands = useOtherPlmBrands();

  return computed(() => {
    const showAdmin = canSeeAdmin();

    const navigate: PaletteEntry[] = [
      {
        id: 'nav-products',
        label: 'Products',
        group: 'Navigate',
        icon: navIcons.products,
        shortcut: 'g p',
        keywords: 'articles styles',
        to: () => ({ name: 'products', query: { view: useUserConfig().settings?.products?.latestView } }),
      },
      {
        id: 'nav-components',
        label: 'Components',
        group: 'Navigate',
        icon: navIcons.components,
        iconScale: componentsIconScale,
        shortcut: 'g c',
        keywords: 'materials fabrics trims',
        to: () => ({ name: 'components' }),
      },
      {
        id: 'nav-samples',
        label: 'Samples',
        group: 'Navigate',
        icon: navIcons.samples,
        shortcut: 'g s',
        keywords: 'sample rounds fittings',
        to: () => ({ name: 'samples' }),
      },
      {
        id: 'nav-orders',
        label: 'Orders',
        group: 'Navigate',
        icon: navIcons.orders,
        shortcut: 'g o',
        keywords: 'purchase orders po',
        to: () => ({ name: 'orders' }),
      },
      {
        id: 'nav-colors',
        label: 'Colors',
        group: 'Navigate',
        icon: navIcons.colors,
        shortcut: 'g l',
        keywords: 'colours palettes pantone swatches',
        to: () => ({ name: 'colors' }),
      },
      {
        id: 'nav-suppliers',
        label: 'Suppliers',
        group: 'Navigate',
        icon: navIcons.suppliers,
        shortcut: 'g u',
        keywords: 'vendors factories manufacturers',
        to: () => ({ name: 'suppliers' }),
      },
      {
        id: 'nav-mcharts',
        label: mChartsLabel.value,
        group: 'Navigate',
        icon: navIcons.measurements,
        shortcut: 'g m',
        keywords: 'measurement charts size specs',
        to: () => ({ name: 'measurement-charts' }),
      },
      ...(showAdmin
        ? [
            {
              id: 'nav-admin',
              label: 'Admin',
              group: 'Navigate',
              icon: navIcons.admin,
              shortcut: 'g a',
              keywords: 'settings configuration',
              to: () => ({ name: 'admin' }),
            } satisfies PaletteEntry,
          ]
        : []),
    ];

    const admin: PaletteEntry[] = showAdmin
      ? adminTabs.value.map((tab) => ({
          id: tab.name,
          label: `Admin: ${tab.title}`,
          group: 'Admin' as const,
          // An admin page has no icon of its own — the admin nav identifies it by the group it
          // sits under, so the palette identifies it the same way.
          icon: adminGroupOf(tab.group)?.icon,
          iconScale: adminGroupOf(tab.group)?.iconScale,
          keywords: `${tab.group} ${tab.keywords}`,
          to: () => ({ name: tab.name }),
        }))
      : [];

    const search: PaletteEntry[] = entitySearchKinds.map((kind) => ({
      id: `search-${kind.type}`,
      label: kind.label,
      group: 'Search' as const,
      icon: kind.icon,
      iconScale: kind.iconScale,
      keywords: kind.keywords,
      shortcut: kind.shortcut,
      enter: { id: 'search', entity: kind.type },
    }));

    // One other brand doesn't deserve a sub-menu — switching to it is the whole menu.
    const brands: PaletteEntry[] =
      otherBrands.value.length === 0
        ? []
        : otherBrands.value.length === 1
          ? [
              {
                id: `brand-${otherBrands.value[0].brandID}`,
                label: `Switch to ${otherBrands.value[0].name}`,
                group: 'Actions',
                icon: navIcons.brand,
                image: brandLogoUrl(otherBrands.value[0].brandID),
                keywords: 'brand company switch',
                run: () => switchBrand(otherBrands.value[0].brandID),
                runInNewTab: () => switchBrandInNewTab(otherBrands.value[0].brandID),
              } satisfies PaletteEntry,
            ]
          : [
              {
                id: 'action-brands',
                label: 'Switch brand',
                group: 'Actions',
                icon: navIcons.brand,
                keywords: `brand company switch ${otherBrands.value.map((b) => b.name).join(' ')}`,
                shortcut: 's b',
                enter: { id: 'brands' },
              } satisfies PaletteEntry,
            ];

    const actions: PaletteEntry[] = [
      ...brands,
      {
        id: 'action-chat',
        label: 'Toggle chat panel',
        group: 'Actions',
        icon: chatIcon,
        shortcut: isMac ? 'meta+alt+c' : 'alt+c',
        run: () => chatPanel.toggleChat(),
      },
      ...(useAgentChat().enabled === true
        ? [
            {
              id: 'action-agent',
              label: 'Open agent chat',
              group: 'Actions',
              icon: chatIcon,
              shortcut: isMac ? 'meta+alt+a' : 'alt+a',
              keywords: 'ai assistant mcp',
              run: () => chatPanel.toggleOn('agent'),
            } satisfies PaletteEntry,
          ]
        : []),
      {
        id: 'action-search',
        label: 'Focus product search',
        group: 'Actions',
        icon: searchIcon,
        shortcut: '/',
        run: () => focusProductSearch(),
      },
      {
        id: 'action-help',
        label: 'Keyboard shortcuts',
        group: 'Actions',
        icon: questionMarkIcon,
        shortcut: '?',
        run: () => shortcutHelp.open(),
      },
    ];

    return [...navigate, ...search, ...admin, ...actions];
  });
}
